import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import logoJMD from "../../../assets/img/logo-jmd.png";
import { getCategoryIdByName } from "../../../utils/categoryResolver";

const BeritaTerbaru = () => {
	const sectionRef = useRef(null);
	const hasFetchedRef = useRef(false);
	const hasLoadedRef = useRef(false);
	const [cards, setCards] = useState([]);
	const [isVisible, setIsVisible] = useState(false);
	const [showSkeleton, setShowSkeleton] = useState(true);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					if (!hasLoadedRef.current) {
						setShowSkeleton(true);
						setTimeout(() => setShowSkeleton(false), 600);
					}
				}
			},
			{ threshold: 0.15 }
		);
		if (sectionRef.current) observer.observe(sectionRef.current);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (!isVisible || hasFetchedRef.current) return;
		const fetchPostsData = async () => {
			try {
				hasFetchedRef.current = true;
				const catId = (await getCategoryIdByName("Berita")) || 2;
				const response = await axios.get(`/api_w/posts`, {
					params: { category_id: catId },
					headers: { db_name: process.env.REACT_APP_DB_NAME },
				});
				const filteredPosts = response.data.data.filter(
					(post) => post.status === 1
				);
				const sortedCards = [...filteredPosts].sort(
					(a, b) => new Date(b.date) - new Date(a.date)
				);
				setCards(sortedCards);
				hasLoadedRef.current = true;
			} catch (error) {
				toast.error(error.response?.data?.message || error.message);
			}
		};
		fetchPostsData();
	}, [isVisible]);

	const formatDate = (dateString) => {
		const options = { year: "numeric", month: "long", day: "numeric" };
		return new Date(dateString).toLocaleDateString("id-ID", options);
	};

	const CardSkeleton = () => (
		<div className="animate-pulse flex gap-4 md:gap-8 items-center bg-white p-4 rounded-3xl border border-gray-100">
			<div className="w-1/3 aspect-square md:aspect-video bg-gray-200 rounded-2xl flex-shrink-0" />
			<div className="flex-1 space-y-3">
				<div className="h-4 w-3/4 bg-gray-200 rounded" />
				<div className="h-3 w-full bg-gray-200 rounded" />
				<div className="h-3 w-2/3 bg-gray-200 rounded" />
				<div className="h-3 w-1/4 bg-gray-200 rounded pt-2" />
			</div>
		</div>
	);

	return (
		<section aria-label="Berita Terbaru" ref={sectionRef} className="py-20 px-4 md:px-20 bg-gray-50">
			<div className="max-w-8xl mx-auto">
				<div className="mb-16">
					<h2 className="text-3xl md:text-5xl font-bold text-gray-800">
						Berita
						<br />
						<span className="text-green-700">Jogja Madrasah Digital</span>
					</h2>
				</div>

				<div className="space-y-6">
					{showSkeleton ? (
						<>
							<CardSkeleton />
							<CardSkeleton />
							<CardSkeleton />
						</>
					) : cards.length === 0 ? (
						<div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
							<svg
								className="w-16 h-16 mx-auto text-gray-300 mb-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
								/>
							</svg>
							<p className="text-gray-500 font-medium text-lg">
								Belum ada berita tersedia
							</p>
							<p className="text-gray-400 text-sm mt-1">
								Berita terbaru akan muncul di sini
							</p>
						</div>
					) : (
						<>
							{cards.slice(0, 3).map((post, idx) => (
								<Link
									key={post.id}
									to={`/berita/${post.slug}`}
									className={`group flex flex-col md:flex-row gap-6 md:gap-10 items-center p-4 md:p-6 rounded-[2rem] hover:bg-green-50/50 border border-transparent hover:border-green-100 transition-all duration-500 ${
										isVisible ? "animate-fade-in-up opacity-100" : "opacity-0"
									}`}
									style={{ animationDelay: `${idx * 0.15}s` }}
								>
									{/* Image */}
									<div className="w-full md:w-[35%] aspect-video md:aspect-[4/3] rounded-[1.5rem] overflow-hidden shadow-md flex-shrink-0">
										<img
											src={post.image || logoJMD}
											alt={post.title}
											className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
											onError={(e) => { e.target.src = logoJMD; e.target.onerror = null; }}
										/>
									</div>

									{/* Content */}
									<div className="flex-1">
										<div className="flex items-center gap-2 mb-3">
											<span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
												News & Updates
											</span>
											<span className="text-[10px] text-gray-400">
												{formatDate(post.date)}
											</span>
										</div>
										<h3 className="text-lg md:text-2xl font-bold text-gray-800 group-hover:text-green-700 transition-colors duration-300 mb-3 leading-tight">
											{post.title}
										</h3>
										<p className="text-sm text-gray-500 leading-relaxed line-clamp-2 md:line-clamp-3 mb-4">
											{typeof post.description === "string"
												? post.description.replace(/<[^>]*>/g, "")
												: ""}
										</p>
										<span className="text-xs font-bold text-green-700 flex items-center gap-1 group-hover:gap-2 transition-all duration-300">
											Baca Selengkapnya
											<svg
												className="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M17 8l4 4m0 0l-4 4m4-4H3"
												/>
											</svg>
										</span>
									</div>
								</Link>
							))}
						</>
					)}
				</div>

				{cards.length > 0 && (
					<div className="mt-16 text-center">
						<Link
							to="/berita"
							className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-3.5 rounded-full shadow-lg shadow-green-600/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
						>
							Lihat Berita Lainnya
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M17 8l4 4m0 0l-4 4m4-4H3"
								/>
							</svg>
						</Link>
					</div>
				)}
			</div>
		</section>
	);
};

export default BeritaTerbaru;
